/**
 * Keep two horizontally scrolling elements at the same scrollLeft — the
 * desktop bracket's sticky round-labels row and its matches scrollport.
 *
 * The link must never write back to the element that started a scroll. The
 * previous version guarded with a `syncing` flag cleared on the next
 * animation frame, but the mirror's scroll event is dispatched a frame AFTER
 * the write, by which time the flag was already cleared. The echo handler
 * then assigned `matches.scrollLeft = labels.scrollLeft` (a frame-old value),
 * and an instant scrollLeft write aborts any in-flight smooth scroll on both
 * axes. That killed the post-pick auto-scroll whenever it had to move
 * sideways — i.e. every jump to the top of the next round column (ticket
 * 74a54965) — leaving the page parked a few pixels from where it started.
 *
 * Instead, remember the value each element was last set to by the link
 * (read back after the write, so browser clamping is accounted for — the
 * matches scrollport has a vertical scrollbar and can scroll a little further
 * than the labels row) and treat a scroll event reporting that value as the
 * echo it is.
 *
 * Returns a cleanup function that removes the listeners.
 */
export function linkHorizontalScroll(primary, mirror) {
  if (!primary || !mirror) return () => {}

  let primaryEcho = null
  let mirrorEcho = null
  const same = (a, b) => b !== null && Math.abs(a - b) < 1

  const copy = (from, to) => {
    if (same(to.scrollLeft, from.scrollLeft)) return null
    to.scrollLeft = from.scrollLeft
    return to.scrollLeft
  }

  const onPrimaryScroll = () => {
    if (same(primary.scrollLeft, primaryEcho)) {
      primaryEcho = null
      return
    }
    primaryEcho = null
    mirrorEcho = copy(primary, mirror)
  }

  const onMirrorScroll = () => {
    if (same(mirror.scrollLeft, mirrorEcho)) {
      mirrorEcho = null
      return
    }
    mirrorEcho = null
    primaryEcho = copy(mirror, primary)
  }

  primary.addEventListener('scroll', onPrimaryScroll, { passive: true })
  mirror.addEventListener('scroll', onMirrorScroll, { passive: true })
  return () => {
    primary.removeEventListener('scroll', onPrimaryScroll)
    mirror.removeEventListener('scroll', onMirrorScroll)
  }
}
